import React, {Component, createRef, RefObject} from 'react';
import './index.scss';
import {Design, DesignScreenGroup, DesignThumbnailElement} from "services/entities/designs";
import {SpacePreview as SpacePreviewEntity, SpacePreviewScreen} from "services/entities/spaces";
import {loadSpacePreviews} from "services/spaces";
import {Button} from "@material-ui/core";
import ProgressSpinner from "components/ProgressSpinner";
import {Matrix3D, Size2D} from "util/geometry";

interface DesignPreviewProps {
  className?: string;
  design?: Design;
}

interface DesignPreviewState {
  previews: SpacePreviewEntity[]|null;
  currentPreview: number;
  previewSize: Size2D|null;
  thumbnailCounter: number;
}

export default class DesignPreview extends Component<DesignPreviewProps, DesignPreviewState> {
  private previewContainer: RefObject<HTMLDivElement> = createRef();
  private thumbnailInterval: any = null;

  state: DesignPreviewState = {
    previews: null,
    currentPreview: 0,
    previewSize: null,
    thumbnailCounter: 0
  };

  componentDidMount() {
    if(this.props.design) {
      loadSpacePreviews(this.props.design.space.code).then(
        previews => {
          this.setState({previews: previews, currentPreview: 0, thumbnailCounter: 0}, () => {
            this.updatePreviewSize();
            this.updateThumbnailTimer();
          });
        }
      );
    }
    window.addEventListener('resize', this.onWindowResize);
  }

  componentWillUnmount() {
    this.stopThumbnailTimer();
    window.removeEventListener('resize', this.onWindowResize);
  }

  private onWindowResize = () => {
    this.updatePreviewSize();
  };

  onPreviewClick = (index: number) => {
    this.setState({currentPreview: index, thumbnailCounter: 0}, () => { this.updatePreviewSize(); });
  };

  onBackgroundLoad = () => {
    this.updatePreviewSize();
  };

  updateThumbnailTimer() {
    const design = this.props.design;
    if(design && design.screenGroups.find(sg => (sg.thumbnailElements?.length || 0) > 0)) {
      this.startThumbnailTimer();
    } else {
      this.stopThumbnailTimer();
    }
  };

  startThumbnailTimer() {
    if(!this.thumbnailInterval) {
      this.thumbnailInterval = setInterval(this.incrementThumbnail, 2000);
    }
  };

  stopThumbnailTimer() {
    if(this.thumbnailInterval) {
      clearInterval(this.thumbnailInterval);
      this.thumbnailInterval = null;
    }
  };

  incrementThumbnail = () => {
    this.setState({thumbnailCounter: this.state.thumbnailCounter + 1});
  };

  updatePreviewSize() {
    const container = this.previewContainer.current;
    if(!container) {
      return;
    }
    const size = {width: container.clientWidth, height: container.clientHeight};
    this.setState({previewSize: size});
  }

  private static getCameraStyle(preview: SpacePreviewEntity, size: Size2D|null) {
    const w = size ? size.width : 1000;
    const h = size ? size.height : 1000;
    const projection = new Matrix3D();
    projection.setProjection(preview.camera.fov, w / h, 0.1, 2000);
    const view = new Matrix3D();
    view.setViewTransform(w, h);
    const transform = Matrix3D.multiplyMatrices(projection, view);
    const matrixCSS = transform.values.map(v => v.toString()).join(",");
    const p = preview.camera.position;
    const r = preview.camera.rotation;
    return {transform: `matrix3d(${matrixCSS}) rotateX(${-r.x}deg) rotateY(${-r.y}deg) rotateZ(${-r.z}deg) translate3d(${-p.x}px, ${-p.y}px, ${-p.z}px) translate(50%, 50%)`};
  }

  private static getScreenGroupStyle(screenGroup: SpacePreviewScreen, index: number) {
    const p = screenGroup.position;
    const r = screenGroup.rotation;
    return { zIndex: Math.round(p.z), width: `${screenGroup.size.width}px`, height: `${screenGroup.size.height}px`,
      transform: `translate(-50%, -50%) translate3d(${p.x}px, ${p.y}px, ${p.z}px) rotateX(${r.x}deg) rotateY(${r.y}deg) rotateX(${r.z}deg)`};
  }

  private static getThumbnailElementStyle(element: DesignThumbnailElement) {
    const bounds = element.bounds;
    const r = element.rotation;
    return { width: `${bounds.width}px`, height: `${bounds.height}px`,
      transform: `translate3d(${bounds.x}px, ${bounds.y}px, 0px) rotateZ(${r}deg)`};
  }

  renderPreviewThumbnailElement(element: DesignThumbnailElement, index: number) {
    const visibleThumbnailIndex = this.state.thumbnailCounter % (element.thumbnails.length + 1);
    return <div key={index} className="thumbnails" style={DesignPreview.getThumbnailElementStyle(element)}>
      { element.thumbnails.map((t, i) => <img src={t} key={i} alt="" className={`thumbnail ${i === visibleThumbnailIndex ? 'current' : ''}`} />) }
    </div>;
  }

  renderPreviewScreen(screen: SpacePreviewScreen, index: number, screenGroup?: DesignScreenGroup) {
    const url = screenGroup?.thumbnailImageURL;
    if(!url) {
      return null;
    }
    return <div className="screen" key={index} style={DesignPreview.getScreenGroupStyle(screen, index)}>
      <img src={url.toString()} alt="" className="hero" />
      { screenGroup?.thumbnailElements && screenGroup.thumbnailElements.map(
        (te, i) => this.renderPreviewThumbnailElement(te, i)) }
    </div>;
  }

  renderPreview(preview: SpacePreviewEntity, size: Size2D|null, design?: Design) {
    return <div className="preview" ref={this.previewContainer}>
      <img src={preview.background.toString()} className="background" alt="" onLoad={this.onBackgroundLoad} />
      <div className="screens" style={DesignPreview.getCameraStyle(preview, size)}>
        { preview.screens.map((s, i) => this.renderPreviewScreen(s, i,
          design ? design.screenGroups.find(sg => sg.screenGroup.code === s.code) : undefined)) }
      </div>
      { preview.foreground && <img src={preview.foreground.toString()} className="foreground" alt="" /> }
    </div>;
  }

  renderPreviewLinks(previews: SpacePreviewEntity[]) {
    const currentIndex = this.state.currentPreview;
    return <ul className="previews-nav">
      { previews.map((p, i) => {
        return <li key={i} className={currentIndex === i ? 'current' : ''}>
          <Button onClick={() => this.onPreviewClick(i)}>{ p.name }</Button>
        </li>;
      })}
    </ul>;
  }

  render() {
    const design = this.props.design;
    const { previews, currentPreview, previewSize } = this.state;
    return <div className={`design-preview ${this.props.className || ''} ${previews ? '' : 'loading'}`}>
      { previews && previews.length > 0 && <>
        { this.renderPreviewLinks(previews) }
        { this.renderPreview(previews[currentPreview], previewSize, design) }
      </> }
      { !previews && <ProgressSpinner /> }
    </div>;
  }
}